import { useEffect, useState } from 'react';
import VoiceProcessingModelControls from 'components/QuestionFlow/VoiceProcessingModelControls';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { useToast } from 'components/ui/use-toast';
import { adminContent } from 'services/api/adminContentClient';
import type { VoiceProcessingModels, VoiceProcessingModelsSetting } from 'types';

export default function VoiceProcessingModelsCard() {
  const { addToast } = useToast();
  const [setting, setSetting] = useState<VoiceProcessingModelsSetting | null>(null);
  const [previousModels, setPreviousModels] = useState<VoiceProcessingModels | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(false);

  useEffect(() => {
    let active = true;
    adminContent.operations.getVoiceProcessingModels().then(({ data }) => {
      if (active) setSetting(data);
    }).catch(() => {
      if (active) addToast({ title: 'Failed to load Voice processing models', variant: 'destructive' });
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [addToast]);

  const update = async (models: Partial<VoiceProcessingModels>) => {
    if (saving || !setting || unconfirmed) return;
    setSaving(true);
    try {
      const { data } = await adminContent.operations.updateVoiceProcessingModels(models);
      setPreviousModels(setting.default_processing_models);
      setSetting(data);
      addToast({ title: 'Voice processing models updated', description: 'New sessions use these defaults. Existing sessions keep their models.' });
    } catch {
      setPreviousModels(null);
      try {
        const { data } = await adminContent.operations.getVoiceProcessingModels();
        setSetting(data);
        addToast({ title: 'Could not confirm the model update', description: 'Reloaded the saved Operations defaults. Review them before making another change.', variant: 'destructive' });
      } catch {
        setUnconfirmed(true);
        addToast({ title: 'Could not confirm the model update', description: 'Saved defaults could not be reloaded. Reload this page before changing models again.', variant: 'destructive' });
      }
    } finally { setSaving(false); }
  };

  return <Card className="w-full max-w-none" maxWidth="none">
    <CardHeader><CardTitle>Voice processing models</CardTitle></CardHeader>
    <CardContent className="space-y-4 items-stretch">
      <p className="text-sm text-muted-foreground">Independent defaults for new module Voice Mode sessions in both Live and Realtime. Existing sessions keep their models; staff can test a session override. These controls do not change Module Companion or Roleplay.</p>
      {loading ? <p>Loading Voice processing models...</p> : setting ? <>
        <VoiceProcessingModelControls models={setting.default_processing_models}
          options={setting.processing_model_options} labelPrefix="Default" disabled={saving || unconfirmed}
          onChange={(models) => { void update(models); }} />
        {previousModels ? <Button type="button" disabled={saving} variant="secondary"
          onClick={() => { void update(previousModels); }}>Restore previous processing defaults</Button> : null}
        {saving ? <p role="status">Saving Voice processing models...</p> : null}
        {unconfirmed ? <p role="alert">The displayed defaults may be out of date. Reload this page before changing models again.</p> : null}
      </> : <p>Voice processing models are unavailable.</p>}
    </CardContent>
  </Card>;
}
